import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api from "@/admin/api";
import { useAuth } from "@/admin/AuthContext";
import { Eyebrow } from "@/components/comiq/Eyebrow";
import { COLORS } from "@/comiq/tokens";
import { WaveBadge } from "@/pages/admin/Dashboard";
import { DeleteDialog } from "@/pages/admin/DeleteDialog";
import { StudyLifecycleDialog } from "@/pages/admin/StudyLifecycleDialog";
import { toast } from "sonner";
import { Plus, Wrench, Trash2, FileCheck, Settings2 } from "lucide-react";

const NEXT = { draft: ["pilot"], pilot: ["live", "draft"], live: ["closed"], closed: ["archived"], archived: [] };
const NEXT_LABEL = { pilot: "Mulai pilot", live: "Go live", draft: "Kembali ke draf", closed: "Tutup gelombang", archived: "Arsipkan" };

function SnapshotCell({ wave }) {
  const ok = wave.snapshot_ok;
  const needs = wave.status === "pilot" || wave.status === "live";
  const color = ok ? "#166534" : needs ? "#B91C1C" : COLORS.textSoft;
  const label = ok ? "Tersimpan" : needs ? "Belum tersimpan" : "—";
  return (
    <td className="px-3 py-3 text-xs" data-testid={`wave-${wave.wave_id}-snapshot-status`}>
      <span className="font-semibold" style={{ color }}>{label}</span>
      {wave.scoring_active && (
        <span className="block text-[11px]" style={{ color: wave.scoring_snapshot_ok ? "#166534" : "#B45309" }}>
          Skor: {wave.scoring_snapshot_ok ? "tersimpan" : "belum tersimpan"}
        </span>
      )}
      {needs && (
        <span className="block text-[11px]" data-testid={`wave-${wave.wave_id}-consent-snapshot`} style={{ color: wave.consent_snapshot_ok ? "#166534" : "#B45309" }}>
          Consent: {wave.consent_snapshot_ok ? "tersimpan" : "belum tersimpan"}
        </span>
      )}
    </td>
  );
}

export default function Waves() {
  const { user } = useAuth();
  const canDelete = user?.role === "platform_admin";
  const [studies, setStudies] = useState([]);
  const [waves, setWaves] = useState([]);
  const [selected, setSelected] = useState(null);
  const [studyTitle, setStudyTitle] = useState("");
  const [waveLabel, setWaveLabel] = useState("");
  const [del, setDel] = useState(null);
  const [studyLc, setStudyLc] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const loadStudies = (archived = showArchived) =>
    api.get(`/admin/studies${archived ? "?include_retired=1" : ""}`).then((r) => setStudies(r.data)).catch(() => {});
  const loadWaves = (studyId) =>
    api.get(studyId ? `/admin/waves?study_id=${studyId}` : "/admin/waves").then((r) => setWaves(r.data)).catch(() => {});

  useEffect(() => {
    loadStudies();
    const s = searchParams.get("study");
    if (s) {
      setSelected(s);
      loadWaves(s);
    } else {
      loadWaves(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pick = (studyId) => {
    setSelected(studyId);
    loadWaves(studyId);
  };

  const addStudy = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/studies", { title: studyTitle, is_demo: true });
      toast.success("Studi dibuat (berlabel demo)");
      setStudyTitle("");
      loadStudies();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal membuat studi");
    }
  };

  const addWave = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/waves", { study_id: selected, label: waveLabel, is_demo: true });
      toast.success("Gelombang dibuat (draf, berlabel demo)");
      setWaveLabel("");
      loadWaves(selected);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal membuat gelombang");
    }
  };

  const transition = async (waveId, to) => {
    try {
      await api.post(`/admin/waves/${waveId}/status`, { to });
      toast.success(`Status → ${to}`);
      loadWaves(selected);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Transisi ditolak");
    }
  };

  const repair = async (waveId) => {
    try {
      await api.post(`/admin/waves/${waveId}/pin-instrument`);
      toast.success("Koneksi instrumen diperbaiki — snapshot tersimpan");
      loadWaves(selected);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal memperbaiki koneksi instrumen");
    }
  };

  const inputCls = "rounded-comiq-sm border bg-white px-3 py-2 text-sm outline-none";
  const inputStyle = { borderColor: COLORS.line, color: COLORS.ink };

  return (
    <div className="p-6">
      <Eyebrow>Program &amp; Periode</Eyebrow>
      <h1 className="font-serif-editorial mt-1 text-3xl" style={{ color: COLORS.ink }}>Studi &amp; Gelombang</h1>
      <p className="mt-1 text-sm" style={{ color: COLORS.textSoft }}>
        Status gelombang: draf → pilot → live → ditutup → arsip. Transisi dicatat di audit. Gelombang live wajib punya
        instrumen published + snapshot tersimpan.
      </p>

      <div className="mt-6 grid gap-4 lg:grid-cols-3">
        <div className="rounded-comiq border bg-comiq-surface-alt p-5" style={{ borderColor: COLORS.line }}>
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold" style={{ color: COLORS.ink }}>Studi</h2>
            <label className="flex items-center gap-1.5 text-xs" style={{ color: COLORS.textSoft }}>
              <input type="checkbox" checked={showArchived} onChange={(e) => { setShowArchived(e.target.checked); loadStudies(e.target.checked); }} data-testid="toggle-archived-studies" />
              Tampilkan arsip
            </label>
          </div>
          <div className="mt-3 space-y-1.5">
            {studies.map((s) => (
              <div key={s.study_id} className="flex items-center gap-1">
              <button
                onClick={() => pick(s.study_id)}
                data-testid={`study-${s.study_id}`}
                className="block flex-1 rounded-comiq-sm border px-3 py-2.5 text-left text-sm transition-colors"
                style={{
                  borderColor: selected === s.study_id ? COLORS.orange : COLORS.line,
                  background: selected === s.study_id ? "rgba(242,84,27,0.06)" : "#fff",
                  color: COLORS.ink,
                }}
              >
                {s.title}
                {s.is_demo && <span className="ml-2 text-[10px] font-bold uppercase" style={{ color: "#B45309" }}>demo</span>}
                {s.status === "retired" && <span className="ml-2 rounded-full px-1.5 py-0.5 text-[10px] font-bold uppercase" style={{ background: "#EEEAE0", color: "#57534E" }} data-testid={`study-retired-${s.study_id}`}>Arsip</span>}
              </button>
              <button
                onClick={() => navigate(`/admin/consents?study=${s.study_id}`)}
                data-testid={`consent-study-${s.study_id}`}
                title="Kelola consent studi"
                className="rounded-comiq-sm border p-2"
                style={{ borderColor: COLORS.line, color: COLORS.ink }}
              >
                <FileCheck size={13} />
              </button>
              {canDelete && (
                <button onClick={() => setStudyLc(s.study_id)} data-testid={`manage-study-${s.study_id}`} title="Arsipkan / Hapus studi" className="rounded-comiq-sm border p-2" style={{ borderColor: "#F3C7C0", color: "#B91C1C" }}>
                  <Settings2 size={13} />
                </button>
              )}
              </div>
            ))}
          </div>
          <form onSubmit={addStudy} className="mt-4 flex gap-2">
            <input className={`${inputCls} flex-1`} style={inputStyle} placeholder="Judul studi baru" value={studyTitle} onChange={(e) => setStudyTitle(e.target.value)} required data-testid="study-title-input" />
            <button className="btn-comiq btn-line !py-2" data-testid="study-add-btn"><Plus size={14} /></button>
          </form>
        </div>

        <div className="rounded-comiq border bg-comiq-surface-alt p-5 lg:col-span-2" style={{ borderColor: COLORS.line }}>
          <h2 className="text-sm font-semibold" style={{ color: COLORS.ink }}>Gelombang {selected ? "" : "(semua studi)"}</h2>
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-sm" data-testid="waves-table">
              <thead>
                <tr style={{ color: COLORS.textSoft }}>
                  {["Label", "Studi", "Status", "Instrumen", "Versi", "Status snapshot", "Dibuka", "Aksi"].map((h) => (
                    <th key={h} className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {waves.map((w) => (
                  <tr key={w.wave_id} className="border-t" style={{ borderColor: COLORS.line }} data-testid={`wave-row-${w.wave_id}`}>
                    <td className="px-3 py-3 font-medium" style={{ color: COLORS.ink }}>
                      {w.label}
                      {w.is_demo && <span className="ml-2 text-[10px] font-bold uppercase" style={{ color: "#B45309" }}>demo</span>}
                    </td>
                    <td className="px-3 py-3 text-xs" data-testid={`wave-${w.wave_id}-study`} style={{ color: COLORS.textSoft }}>
                      {w.study_title || "—"}
                    </td>
                    <td className="px-3 py-3"><WaveBadge status={w.status} /></td>
                    <td className="px-3 py-3 text-xs" data-testid={`wave-${w.wave_id}-instrument`} style={{ color: w.snapshot_ok ? COLORS.ink : "#B45309" }}>
                      {w.instrument_title || (w.status === "live" || w.status === "pilot" ? "Belum terhubung" : "—")}
                    </td>
                    <td className="px-3 py-3 text-xs" data-testid={`wave-${w.wave_id}-version`} style={{ color: COLORS.textSoft }}>
                      {w.instrument_version_no ? `v${w.instrument_version_no}` : "—"}
                    </td>
                    <SnapshotCell wave={w} />
                    <td className="px-3 py-3 text-xs" style={{ color: COLORS.textSoft }}>{w.open_at ? new Date(w.open_at).toLocaleDateString("id-ID") : "—"}</td>
                    <td className="px-3 py-3">
                      <div className="flex flex-wrap gap-2">
                        {(NEXT[w.status] || []).map((to) => (
                          <button
                            key={to}
                            onClick={() => transition(w.wave_id, to)}
                            data-testid={`wave-${w.wave_id}-to-${to}`}
                            className="rounded-full border px-2.5 py-1 text-xs font-semibold transition-colors"
                            style={{ borderColor: COLORS.lineStrong, color: COLORS.ink }}
                          >
                            {NEXT_LABEL[to]}
                          </button>
                        ))}
                        {(w.status === "pilot" || w.status === "live") && !w.snapshot_ok && (
                          <button
                            onClick={() => repair(w.wave_id)}
                            data-testid={`wave-${w.wave_id}-repair-instrument`}
                            className="inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-semibold transition-colors"
                            style={{ borderColor: "#B45309", color: "#B45309" }}
                          >
                            <Wrench size={12} /> Perbaiki koneksi instrumen
                          </button>
                        )}
                        {canDelete && ["draft", "pilot", "archived"].includes(w.status) && (
                          <button
                            onClick={() => setDel({ kind: "wave", id: w.wave_id })}
                            data-testid={`delete-wave-${w.wave_id}`}
                            className="inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-semibold"
                            style={{ borderColor: "#F3C7C0", color: "#B91C1C" }}
                          >
                            <Trash2 size={12} /> Hapus
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {selected && (
            <form onSubmit={addWave} className="mt-4 flex gap-2">
              <input className={`${inputCls} flex-1`} style={inputStyle} placeholder="Label gelombang (mis. 2026-W2)" value={waveLabel} onChange={(e) => setWaveLabel(e.target.value)} required data-testid="wave-label-input" />
              <button className="btn-comiq btn-accent" data-testid="wave-add-btn"><Plus size={15} /> Buat gelombang</button>
            </form>
          )}
          {!selected && <p className="mt-3 text-xs" style={{ color: COLORS.textSoft }}>Pilih studi untuk menambah gelombang baru.</p>}
        </div>
      </div>
      {del && <DeleteDialog kind={del.kind} id={del.id} onClose={() => setDel(null)} onDeleted={() => { loadStudies(); loadWaves(selected); }} />}
      {studyLc && <StudyLifecycleDialog id={studyLc} onClose={() => setStudyLc(null)} onDone={() => { loadStudies(); loadWaves(selected); }} />}
    </div>
  );
}
